import { useDropzone } from 'react-dropzone'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { UUID } from 'crypto'

import { useQueryClient } from '@tanstack/react-query'
import { UploadOrgDocsType } from '@/models/Orgs'
import { dedupeDroppedFiles } from '@/utils/uploadDedup'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { useOnboardingData } from '@/utils/hooks/onboarding'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { useSSEProgress } from '@/pages/onboarding/hooks/useSSEProgress'
import {
  UPLOAD_ACCEPT,
  ALLOWED_FILE_TYPES_LABEL,
  describeRejectionReason,
} from '@/utils/uploadFileTypes'

const MAX_FILES = Number(import.meta.env.VITE_MAX_FILES_PER_UPLOAD) || 15

export default function UploadFile({
  orgUuid,
  token,
  uploadOrgDocs,
  disabled,
  onUnsavedChange,
  existingDocNames = [],
}: {
  orgUuid: UUID
  token: string
  uploadOrgDocs: UploadOrgDocsType
  disabled?: boolean
  onUnsavedChange?: (hasUnsaved: boolean) => void
  existingDocNames?: string[]
}) {
  const [files, setFiles] = useState<File[]>([])
  const [uploaded, setUploaded] = useState<File[] | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dropError, setDropError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const { isOnboarding } = useOnboardingStatus()
  const navigate = useNavigate()
  const { user, token: userToken } = getCurrentUser()
  const { data: onboardingData } = useOnboardingData({
    user_uuid: user?.uuid ?? null,
    token: userToken,
    selected_org_uuid: user?.selected_org_uuid ?? undefined,
  })
  const hasGeneratedProfile = onboardingData?.has_profile ?? false
  const { canWrite: canWriteSubscription } = usePausedOrg()
  const canWrite = canWriteSubscription && !disabled
  const queryClient = useQueryClient()
  const uploadProgress = useSSEProgress({
    type: 'upload',
    jobId,
    token,
    onComplete: () => {
      setUploaded(pendingFiles)
      setFiles([])
      setPendingFiles([])
      setUploading(false)
      setJobId(null)
      setDropError(null)
      queryClient.invalidateQueries({ queryKey: ['orgDocs'] })
      // The worker kicks off a profile regen once the docs are saved. Refetch so
      // the profile page picks up the in_progress status without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['currentOrgs', user?.uuid] })
    },
    // Ingestion failures publish the worker's raw exception text (app/worker.py's
    // _handle_file_upload) — none of it is curated for end users, so always show
    // a generic message rather than passing it through.
    onError: () => {
      setUploadError('Error uploading files')
      setUploading(false)
      setJobId(null)
    },
  })

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Reads `files` from the closure rather than a setFiles(prev => ...) updater,
      // so setDropError below isn't called from inside a state updater (updaters must
      // be pure and can otherwise fire twice under StrictMode).
      // Skip files whose name already appears in the staging list or the org's
      // already-uploaded docs.
      const { accepted, duplicates } = dedupeDroppedFiles(
        acceptedFiles,
        files.map((f) => f.name),
        existingDocNames,
      )
      const newTotalCount = files.length + accepted.length
      if (newTotalCount > MAX_FILES) {
        setDropError(
          `Maximum files that can be uploaded at one time is ${MAX_FILES}`,
        )
        return
      }
      const messages: string[] = []
      if (rejectedFiles.length > 0) {
        messages.push(
          `${rejectedFiles.map((r) => r.file.name).join(', ')} - ${describeRejectionReason(rejectedFiles[0])} Allowed types: ${ALLOWED_FILE_TYPES_LABEL}.`,
        )
      }
      if (duplicates.length > 0) {
        messages.push(
          `Skipped ${duplicates.join(', ')} — already added or uploaded.`,
        )
      }
      setDropError(messages.length > 0 ? messages.join('\n') : null)
      if (accepted.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...accepted])
      }
    },
    accept: UPLOAD_ACCEPT,
  })

  // Report staged-file state up to ProfilePage's unsaved registry. onUnsavedChange is
  // a fresh inline callback each render, so keep it in a ref and depend on the value.
  const onUnsavedChangeRef = useRef(onUnsavedChange)
  onUnsavedChangeRef.current = onUnsavedChange
  useEffect(() => {
    onUnsavedChangeRef.current?.(files.length > 0)
    return () => onUnsavedChangeRef.current?.(false)
  }, [files])

  async function handleUpload() {
    setUploading(true)
    setUploaded(null)
    setUploadError(null)
    setDropError(null)

    try {
      const response = await uploadOrgDocs.mutateAsync({
        token,
        files,
        orgUuid,
      })
      setPendingFiles(files)
      setJobId(response.job_id)
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Error uploading files',
      )
      setUploading(false)
    }
  }

  function handleRemoveFile(index: number) {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    setDropError(null)
    setUploadError(null)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" mb={2}>
        Upload Files
      </Typography>
      <Paper
        {...(!canWrite ? {} : getRootProps())}
        sx={{
          p: 3,
          textAlign: 'center',
          border: '2px dashed #ccc',
          borderRadius: 2,
          backgroundColor: canWrite
            ? isDragActive
              ? '#f0f8ff'
              : '#fafafa'
            : '#f5f5f5',
          cursor: canWrite ? 'pointer' : 'not-allowed',
          boxShadow: 'none',
          opacity: !canWrite ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} disabled={!canWrite} />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UploadFileIcon sx={{ mr: 1, fontSize: 40, color: '#1976d2' }} />

          {isMobile
            ? 'Tap here to upload'
            : 'Drag and drop files here or click to upload'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Max file size: 25MB. Max files per upload: {MAX_FILES}. Allowed types:{' '}
          {ALLOWED_FILE_TYPES_LABEL}.
        </Typography>
      </Paper>

      {dropError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setDropError(null)}
        >
          <Box
            component="pre"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}
          >
            {dropError}
          </Box>
        </Alert>
      )}

      {files.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1">Files to upload:</Typography>
          <List>
            {files.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveFile(index)}
                    disabled={uploading}
                    aria-label={`Remove ${file.name} file from upload list`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disableRipple
              onClick={handleUpload}
              disabled={uploading || !canWrite}
              sx={{
                mt: 2,
                mx: 'auto',
                width: '100%',
                maxWidth: uploading ? 500 : 300,
                bgcolor: '#1C3045',
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: 2,
                gap: 2,
                fontSize: '16px',
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <span style={{ color: '#636363' }}>
                    Uploading files... {uploadProgress.progress}%
                  </span>
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Box>
          {uploading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please do not leave this page while the upload is in progress.
            </Alert>
          )}
        </Box>
      )}

      {uploaded && files.length === 0 && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Files uploaded successfully!
        </Alert>
      )}
      {uploaded &&
        !uploading &&
        !uploadError &&
        files.length === 0 &&
        (!isOnboarding || hasGeneratedProfile) && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
            }}
            action={
              !isOnboarding && (
                <Button
                  variant="contained"
                  size="small"
                  disableRipple
                  onClick={() => navigate('/profile/funding-profile')}
                  aria-label="Review the changes to your funding profile"
                  sx={{
                    color: 'white',
                    bgcolor: '#1C3045',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#152431' },
                  }}
                >
                  Review changes
                </Button>
              )
            }
          >
            We're updating your funding profile with this new information — you
            can review the changes when it's ready.
          </Alert>
        )}
      {uploadError && (
        <Alert
          severity="error"
          sx={{ mt: 3 }}
          onClose={() => setUploadError(null)}
        >
          {uploadError}
        </Alert>
      )}
    </Box>
  )
}
